/**
 * TrackImporter — normalises flight-track files (GPX, KML, CSV, TSV,
 * GeoJSON) into a common Track shape.
 *
 * Strategy:
 *   1. Detect format from extension (fallback to content sniffing).
 *   2. Try DuckDB-wasm + spatial extension (SQL-native, also does
 *      airspace joins later).
 *   3. If DuckDB fails (CSP, test env, offline-no-CDN), fall back to the
 *      pure-TS parsers in `track-parsers/`.
 *   4. Post-process: compute TrackStats uniformly so both paths emit the
 *      same numbers.
 *   5. Persist via CacheService (indexeddb) so the Flight Review panel
 *      can list previously uploaded tracks.
 */
import { Logger } from './Logger';
import { computeTrackStats } from './TrackStatsCalculator';
import {
  deleteUserData,
  deleteMetadata,
  evictExpired,
  getMetadata,
  getUserData,
  listValues,
  storeUserData,
} from './CacheService';
import {
  detectFormatFromContent,
  detectFormatFromFilename,
  type Track,
  type TrackFormat,
  type TrackPoint,
} from './track-parsers/track-types';
import { parseGpx } from './track-parsers/gpx-parser';
import { parseKml } from './track-parsers/kml-parser';
import { parseCsv, parseTsv } from './track-parsers/csv-parser';
import { parseGeoJson } from './track-parsers/geojson-parser';

export const TRACK_METADATA_PREFIX = 'track:';

export interface ImportOptions {
  /** Force use of the pure-TS parsers even when DuckDB is available. */
  useDuckDB?: boolean;
  /** Persist the parsed Track to IndexedDB (default: true). */
  persist?: boolean;
  /** Override the display name (default: filename without extension). */
  name?: string;
}

function randomId(): string {
  const g: any = globalThis as any; // eslint-disable-line @typescript-eslint/no-explicit-any
  if (g?.crypto?.randomUUID) return g.crypto.randomUUID();
  return `track-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function readFileAsText(file: File): Promise<string> {
  return file.text ? file.text() : new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
}

function baseName(filename: string): string {
  return filename.replace(/\.[^.]+$/, '');
}

function parseNative(content: string, format: TrackFormat): { name?: string; points: TrackPoint[] } {
  switch (format) {
    case 'gpx':     return parseGpx(content);
    case 'kml':     return parseKml(content);
    case 'csv':     return parseCsv(content);
    case 'tsv':     return parseTsv(content);
    case 'geojson': return parseGeoJson(content);
  }
}

export async function detectFormat(file: File): Promise<TrackFormat> {
  const byExt = detectFormatFromFilename(file.name);
  if (byExt) return byExt;
  const head = await readFileAsText(file);
  const byContent = detectFormatFromContent(head);
  if (byContent) return byContent;
  throw new Error(`Unrecognised track format: ${file.name}`);
}

/**
 * Import a track file and return the parsed Track. Always runs the stats
 * calculator at the end so both DuckDB and native paths produce uniform
 * summary numbers.
 */
export async function importTrack(file: File, options: ImportOptions = {}): Promise<Track> {
  const format = await detectFormat(file);
  const displayName = options.name ?? baseName(file.name);
  let points: TrackPoint[] = [];
  let nameFromParser: string | undefined;

  if (options.useDuckDB !== false) {
    try {
      const { importWithDuckDB } = await import('./TrackImporterDuckDB');
      points = await importWithDuckDB(file, format);
    } catch (e) {
      Logger.warn('DuckDB track import failed; falling back to native parser', {
        format, error: (e as Error).message,
      });
    }
  }

  if (points.length === 0) {
    const content = await readFileAsText(file);
    const parsed = parseNative(content, format);
    points = parsed.points;
    nameFromParser = parsed.name;
  }

  if (points.length === 0) {
    throw new Error(`Parsed 0 points from ${file.name}; file may be empty or malformed.`);
  }

  const track: Track = {
    id: randomId(),
    name: options.name ?? nameFromParser ?? displayName,
    format,
    importedAt: Date.now(),
    points,
    stats: computeTrackStats(points),
    sourceFilename: file.name,
  };

  if (options.persist !== false) {
    try {
      await storeUserData(`${TRACK_METADATA_PREFIX}${track.id}`, track);
    } catch (e) {
      Logger.warn('Failed to persist track to IndexedDB', { id: track.id, error: (e as Error).message });
    }
  }

  return track;
}

/** Retrieve a previously imported track by id, or null if absent. */
export async function loadTrack(id: string): Promise<Track | null> {
  const key = `${TRACK_METADATA_PREFIX}${id}`;
  const stored = await getUserData<Track>(key);
  if (stored) return stored;
  const legacy = await getMetadata<Track>(key);
  if (legacy) {
    await storeUserData(key, legacy);
    await deleteMetadata(key);
  }
  return legacy;
}

/** List summaries of every persisted track (lightweight \u2014 excludes points). */
export async function listTracks(): Promise<Array<Pick<Track, 'id' | 'name' | 'format' | 'importedAt' | 'stats' | 'sourceFilename'>>> {
  const out: Array<Pick<Track, 'id' | 'name' | 'format' | 'importedAt' | 'stats' | 'sourceFilename'>> = [];
  const stored = await listValues<Track>('userData', TRACK_METADATA_PREFIX);
  const legacy = await listValues<Track>('metadata', TRACK_METADATA_PREFIX);
  const byId = new Map<string, Track>();
  for (const entry of [...legacy, ...stored]) byId.set(entry.value.id, entry.value);
  for (const track of byId.values()) {
    out.push({
      id: track.id, name: track.name, format: track.format,
      importedAt: track.importedAt, stats: track.stats,
      sourceFilename: track.sourceFilename,
    });
    if (!stored.some((entry) => entry.value.id === track.id)) {
      const key = `${TRACK_METADATA_PREFIX}${track.id}`;
      await storeUserData(key, track);
      await deleteMetadata(key);
    }
  }
  out.sort((a, b) => b.importedAt - a.importedAt);
  return out;
}

/** Remove a persisted track. */
export async function deleteTrack(id: string): Promise<void> {
  const key = `${TRACK_METADATA_PREFIX}${id}`;
  await deleteUserData(key);
  await deleteMetadata(key);
}

/** Maintenance hook: drop persisted tracks older than `olderThanDays`. */
export async function evictOldTracks(olderThanDays = 90): Promise<number> {
  const olderThanMs = olderThanDays * 24 * 60 * 60 * 1000;
  return (await evictExpired('userData', olderThanMs, TRACK_METADATA_PREFIX))
    + (await evictExpired('metadata', olderThanMs, TRACK_METADATA_PREFIX));
}
